export const SOLD_BY = {
  EACH: 'EACH',
  WEIGHT: 'WEIGHT',
};

/** Missing or unknown sold_by is Pack. Never infer from unit, name, or category. */
export function soldBy(item) {
  return String(item?.soldBy || item?.sold_by || 'EACH').toUpperCase() === 'WEIGHT'
    ? SOLD_BY.WEIGHT
    : SOLD_BY.EACH;
}

export function isSoldByWeight(product) {
  return soldBy(product) === SOLD_BY.WEIGHT;
}

export function formatPrice(item, formatCurrency) {
  const price = Number(item?.selling_price ?? item?.price);
  const formatted = formatCurrency ? formatCurrency(price) : `₹${price}`;
  return isSoldByWeight(item) ? `${formatted} / ${item.unit || 'kg'}` : formatted;
}

/** Sold qty: 3 dp kg for loose, whole units for packs. */
export function formatQty(qty, item) {
  if (isSoldByWeight(item)) {
    const n = Number(qty);
    const unit = item?.unit || 'kg';
    if (!Number.isFinite(n)) return `0.000 ${unit}`;
    return `${n.toFixed(3)} ${unit}`;
  }
  return String(qty);
}

/** On-hand stock: 25.5 kg vs 25. Does not force 3 dp on whole kg. */
export function formatStock(qty, item) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return '—';
  if (!isSoldByWeight(item)) return String(n);
  const rounded = Math.round(n * 1000) / 1000;
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return `${text} ${item?.unit || 'kg'}`;
}

export function formatWeightKg(qty, unit = 'kg') {
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) return `0.000 ${unit}`;
  return `${n.toFixed(3)} ${unit}`;
}

export function gramsToKg(g) {
  return Math.round(Number(g) || 0) / 1000;
}

export function quantizeKg(qty) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 1000) / 1000;
}

/** Match server: ROUND_HALF_UP to 0.01 (paise). */
export function roundMoney(amount) {
  return Math.round((Number(amount) || 0) * 100) / 100;
}

export function lineAmount(price, qty) {
  return roundMoney((Number(price) || 0) * (Number(qty) || 0));
}

export function cartQtyFooter(lines = []) {
  let pcs = 0;
  let kg = 0;
  for (const line of lines) {
    if (isSoldByWeight(line)) kg += Number(line.qty) || 0;
    else pcs += Number(line.qty) || 0;
  }
  const parts = [];
  if (pcs) parts.push(`${pcs} pcs`);
  if (kg) parts.push(`${kg.toFixed(2)} kg`);
  return parts.join(' + ') || `${lines.length} lines`;
}

/** Receipt footer. Never sums packs and kg into one number. */
export function formatItemsQtySummary(lineItems = []) {
  const items = lineItems.length;
  if (!items) return '0';
  return `${items} / ${cartQtyFooter(lineItems)}`;
}

export function unitPriceLabel(line, formatCurrency) {
  return formatPrice({
    ...line,
    price: line.unit_price ?? line.price,
  }, formatCurrency);
}

export function formatLineQty(line) {
  return formatQty(line.qty, line);
}

export function formatEscPosQtyLine(item, formatMoney) {
  if (isSoldByWeight(item)) {
    const unit = item.unit || 'kg';
    return `  ${Number(item.qty).toFixed(3)} ${unit} x ${formatMoney(item.unit_price)}/${unit} = ${formatMoney(item.line_total)}\n`;
  }
  return `  ${item.qty} x ${formatMoney(item.unit_price)} = ${formatMoney(item.line_total)}\n`;
}

export function qtyOnStep(qty, step) {
  const s = Number(step);
  if (!Number.isFinite(s) || s <= 0) return true;
  const q = Math.round(Number(qty) * 1000);
  const st = Math.round(s * 1000);
  if (st <= 0) return true;
  return q % st === 0;
}

/** Same wording as a rejected charge, checked before the line can be billed. */
export function orderQtyProblem(qty, { minOrderQty, maxOrderQty, qtyStep } = {}) {
  const q = Number(qty);
  if (!Number.isFinite(q) || q <= 0) return '';
  const min = Number(minOrderQty);
  const max = Number(maxOrderQty);
  const step = Number(qtyStep);
  if (Number.isFinite(min) && min > 0 && q < min) return `Need at least ${min} kg / pcs.`;
  if (Number.isFinite(max) && max > 0 && q > max) return `Cap ${max} kg / pcs.`;
  if (Number.isFinite(step) && step > 0 && !qtyOnStep(q, step)) return `Use steps of ${step}.`;
  return '';
}

export function cartQtyIssue(lines) {
  for (const line of lines || []) {
    const problem = orderQtyProblem(line?.qty, line);
    if (problem) return `${line?.name || 'Item'}: ${problem}`;
  }
  return '';
}

export function stepLabel(step, unit = 'kg') {
  const s = Number(step);
  if (!Number.isFinite(s) || s <= 0) return '';
  if (s < 1) return `${Math.round(s * 1000)} g`;
  return `${s} ${unit}`;
}

export function orderLineCopy(line, formatCurrency) {
  const qty = formatQty(line.qty, line);
  const price = formatPrice({
    ...line,
    price: line.price ?? line.unit_price,
  }, formatCurrency);
  return isSoldByWeight(line) ? `${qty} × ${price}` : `${qty} × ${price}`;
}
